import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { apiDelete, apiGet, apiPost } from "../lib/api";
import { useAuth } from "../context/AuthProvider";

type Relation = "none" | "i_blocked" | "blocked_me" | "both";

type Props = {
  targetUserId: string;
  includeBlockedList?: boolean;
  hideBlock?: boolean;
  className?: string;
};

type MenuPos = { top: number; left: number };

const MENU_WIDTH = 240;
const GUTTER = 8;
const BTN_TO_MENU_GAP = 6;

export default function MoreMenuForUser({
  targetUserId,
  includeBlockedList = false,
  hideBlock = false,
  className,
}: Props) {
  const nav = useNavigate();
  const { user: me } = useAuth();
  const isMe = !!(me && targetUserId && String(me.id) === String(targetUserId));

  const [open, setOpen] = useState(false);
  const [relation, setRelation] = useState<Relation>("none");
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState<MenuPos | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const btnRef  = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // İlişki durumunu çek (sadece oturum varsa ve blok menüsü açıksa)
  useEffect(() => {
    let mounted = true;
    // Giriş yapmamışsa API çağrısı yapma
    if (!hideBlock && me && targetUserId) {
      apiGet<{ ok: true; relation: Relation }>(`/api/blocks/status/${targetUserId}`)
        .then((r) => mounted && setRelation(r.relation))
        .catch(() => {});
    }
    return () => { mounted = false; };
  }, [targetUserId, hideBlock, me]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node | null;
      if (!t) return;
      const inRoot = rootRef.current?.contains(t);
      const inMenu = menuRef.current?.contains(t);
      if (!inRoot && !inMenu) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    function clamp(n: number, min: number, max: number) {
      return Math.max(min, Math.min(max, n));
    }
    function place() {
      const btn = btnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const vw = window.innerWidth;

      const top = Math.round(r.bottom + BTN_TO_MENU_GAP);
      let left = Math.round(r.right - MENU_WIDTH);
      left = clamp(left, GUTTER, vw - MENU_WIDTH - GUTTER);

      setPos({ top, left });
    }
    if (open) {
      place();
      window.addEventListener("scroll", place, true);
      window.addEventListener("resize", place);
      return () => {
        window.removeEventListener("scroll", place, true);
        window.removeEventListener("resize", place);
      };
    }
  }, [open]);

  async function toggleBlock() {
    if (loading) return;
    setLoading(true);
    try {
      if (relation === "i_blocked" || relation === "both") {
        await apiDelete(`/api/blocks/${targetUserId}`);
        setRelation("none");
      } else {
        await apiPost(`/api/blocks/${targetUserId}`, {});
        setRelation(relation === "blocked_me" ? "both" : "i_blocked");
      }
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }

  const blockLabel =
    relation === "i_blocked" || relation === "both" ? "Engeli Kaldır"
    : relation === "blocked_me" ? "Seni Engellemiş"
    : "Engelle";
  const blockDisabled = loading || relation === "blocked_me";

  return (
    <div
      ref={rootRef}
      className={className}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        ref={btnRef}
        type="button"
        aria-label="Daha fazla"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          if (!me) {
            nav('/login');
            return;
          }
          setOpen(v => !v);
        }}
        style={{
          width: 32,
          height: 32,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 8,
          border: "2px solid #114232",
          background: "#114232",
          color: "#fff",
          cursor: "pointer",
          lineHeight: 1,
          fontSize: 18,
          fontWeight: 700,
          userSelect: "none",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = "0.85";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
      >
        ⋮
      </button>

      {open && pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: MENU_WIDTH,
              borderRadius: 10,
              zIndex: 2147483647,
              background: "#2b2b2b",
              color: "#eaeaea",
              border: "1px solid #3a3a3a",
              boxShadow: "0 10px 28px rgba(0,0,0,.45)",
              whiteSpace: "nowrap",
              padding: 0,
              overflow: "visible",
            }}
          >
            {isMe && (
              <button
                onClick={() => { setOpen(false); nav("/settings/profile"); }}
                style={menuItemStyle(false)}
              >
                Profil Ayarları
              </button>
            )}

            {includeBlockedList && (
              <button
                onClick={() => { setOpen(false); nav("/settings/blocked"); }}
                style={menuItemStyle(false)}
              >
                Engellenen listesi
              </button>
            )}

            {!hideBlock && (
              <button
                onClick={toggleBlock}
                disabled={blockDisabled}
                style={menuItemStyle(blockDisabled)}
              >
                {loading ? "İşleniyor..." : blockLabel}
              </button>
            )}
          </div>,
          document.body
        )
      }
    </div>
  );
}

function menuItemStyle(disabled: boolean): React.CSSProperties {
  return {
    width: "100%",
    height: 40,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "0 12px",
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    cursor: disabled ? "not-allowed" : "pointer",
    color: disabled ? "#9a9a9a" : "#eaeaea",
    fontSize: 14,
    lineHeight: 1.3,
  };
}
